import * as THREE from 'three';
import {TransformControls, TransformControlsMouseCallback, TransformControlsMoveCallback} from './TransformControls';
import Engine from './Engine';
import Input from './Input';
import WorkspaceManager from './WorkspaceManager';
import Scenario from './scenarioElements/Scenario';
import WorkspaceUtils from './utilities/WorkspaceUtils';
import store, { StoreActions } from '../state/store';
import { RenderLayers, CustomEventTypes, GizmoModes, Space, GizmoPivotMode} from '../globals';
import { KeyCodes, ModifierKeyCodes, MouseButtons } from '../interfaces/Input';

export default class GizmoManager
{
	control: TransformControls;
	initialContext: any;
	translationSnap!: number;
	rotationSnap!: number;
	scaleSnap!: number;

	constructor()
	{
		this.control = new TransformControls();
		this.control.traverse( (child)=>
		{
			child.layers.disableAll();
			child.layers.enable(RenderLayers.MainCameraOnly);
			child.layers.enable(RenderLayers.ModelTrackerPreview);
		});
		const control = this.control;

		document.addEventListener(CustomEventTypes.objectSelectionChanged,  (e: Event)=>
		{
			const customEvent = e as CustomEvent;
			if(store.getState().applicationModule?.isViewOnlyMode) return;
			if (customEvent?.detail?.fromUpdate) 
			{
				return;
			}
			this.setObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects);
		});

		document.addEventListener(CustomEventTypes.selectedObjectPositionChanged,  (e)=>
		{
			if (!control.freeMoveMode) control.setDummyTransform();
		});

		document.addEventListener(CustomEventTypes.selectedObjectRotationChanged,  (e)=>
		{
			control.setDummyTransform();
		});

		document.addEventListener(CustomEventTypes.selectedObjectScaleChanged,  (e)=>
		{
			control.setDummyTransform();
		});

		let preSimulationType;
		document.addEventListener(CustomEventTypes.simulationStarted,  (e)=>
		{
			preSimulationType = this.getMode();
			this.setMode(GizmoModes.Hidden);
		});
		document.addEventListener(CustomEventTypes.simulationStopped,  (e)=>
		{
			this.setMode(preSimulationType);
		});

		this.setTranslationSnap(0.1);
		this.setRotationSnap(45);
		this.setScaleSnap(0.1);
		this.initialContext = null;
	}

	setScene(scene: THREE.Scene): void
	{
		scene.add(this.control);
		scene.add(this.control.dummyObject);
	}

	setCamera(camera: THREE.Camera): void
	{
		this.control.camera = camera;
	}

	getCamera(): THREE.Camera | undefined
	{
		return this.control.camera;
	}

	setObjects(objects: THREE.Object3D[]): void
	{
		this.control.setObjects(objects);
	}

	isBusy(): boolean
	{
		return this.control.dragging;
	}

	update(): void
	{
		if (this.isBusy())
		{
			if (!this.initialContext)
			{
				const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
				const sceneViewData = WorkspaceManager.getSceneViewData(hoveredWindowId);
				const poseEditorData = WorkspaceManager.getModelTrackerPoseEditorData();
				this.initialContext = poseEditorData?.context? poseEditorData.context : sceneViewData?.context ? sceneViewData.context : null;
			}

			if (this.initialContext) this.control.pointerMove(Input.getMousePositionInRect(this.initialContext.canvas.getBoundingClientRect()));
			this.control.setGridSnapMode(Input.getModifier(ModifierKeyCodes.SHIFT));
			if (Input.getMouseButtonUp(MouseButtons.LEFT))
			{
				this.control.pointerUp();
			}

			if (Input.getModifier(ModifierKeyCodes.CTRL) || Input.getModifier(ModifierKeyCodes.COMMAND))
			{
				this.control.setTranslationSnap(this.translationSnap);
				this.control.setRotationSnap(this.rotationSnap);
				this.control.setScaleSnap(this.scaleSnap);
			}
			else
			{
				this.control.setTranslationSnap(null);
				this.control.setRotationSnap(null);
				this.control.setScaleSnap(null);
			}

		}
		else if (!Input.getMouseButton(MouseButtons.RIGHT) && !Input.getModifier(ModifierKeyCodes.ALT))
		{
			this.initialContext = null;
			const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
			const hoveredSceneViewData = WorkspaceManager.getSceneViewData(hoveredWindowId);
			const poseEditorData = WorkspaceManager.getModelTrackerPoseEditorData();
			const activeContext = poseEditorData?.context? poseEditorData.context : hoveredSceneViewData?.context ? hoveredSceneViewData.context : null;
			const contextCamera = poseEditorData?.context? poseEditorData.tracker.modelTrackerCamera : hoveredSceneViewData?.context ? hoveredSceneViewData.activeCamera : null;
			if (activeContext)
			{
				const currentCamera = this.getCamera();
				this.setCamera(contextCamera);
				this.control.updateMatrixWorld();
				this.control.pointerHover(Input.getMousePositionInRect(activeContext.canvas.getBoundingClientRect()));
				if (Input.getMouseButtonDown(MouseButtons.LEFT))
				{
					this.control.pointerDown(Input.getMousePositionInRect(activeContext.canvas.getBoundingClientRect()));
				}
				if (!this.isBusy() && currentCamera)
				{
					this.setCamera(currentCamera);
				}
			}

			if (!WorkspaceUtils.shouldAllowNativeBehavior())
			{
				if (Input.getKeybindDown(Input.KeyBindings.GizmoModeTranslate))
				{
					this.setMode( GizmoModes.Translate);
				}
				else if (Input.getKeybindDown(Input.KeyBindings.GizmoModeRotate))
				{
					this.setMode( GizmoModes.Rotate);
				}
				else if (Input.getKeybindDown(Input.KeyBindings.GizmoModeScale))
				{
					this.setMode( GizmoModes.Scale);
				}
				else if (Input.getKeybindDown(Input.KeyBindings.GizmoModeSpaceToggle))
				{
					if(!this.control.freeMoveMode) this.setSpace(this.control.space === Space.Local ? Space.World : Space.Local);
				}
				else if (Input.getKeybindDown(Input.KeyBindings.GizmoModePivotToggle))
				{
					this.setPivotMode(this.control.pivotMode === GizmoPivotMode.Group ? GizmoPivotMode.Object : GizmoPivotMode.Group);
				}
				else if (Input.getKeybindDown(Input.KeyBindings.GizmoHide))
				{
					this.setMode(GizmoModes.Hidden);
				}
				else if (Input.getKeyDown(KeyCodes.ESCAPE) && this.control.freeMoveMode)
				{
					this.control.setDummyTransform();
				}
			}
		}
	}

	enterFreeMoveMode(mouseDownCallback?: TransformControlsMouseCallback,
					  mouseMoveCallback?: TransformControlsMoveCallback,
					  mouseUpCallback?: TransformControlsMouseCallback, initialGizmoOffset?: THREE.Vector3): void
	{
		const currentStep = Scenario.currentStep();
		if(currentStep?.timeline) store.dispatch(StoreActions.timeline.updateEditorState({ isRecording: false, isPlaying: false }));

		this.setMode(GizmoModes.Translate);
		this.setSpace(Space.World);
		if ( mouseDownCallback && mouseMoveCallback && mouseUpCallback)
		{
			this.control.enterFreeMoveMode(mouseDownCallback, mouseMoveCallback, mouseUpCallback, initialGizmoOffset);
		}
		this.setQuickDuplicationEnabled(false);
	}

	exitFreeMoveMode(): void
	{
		if(this.getFreeMoveMode())
		{
			this.control.setDummyTransform();
			this.setQuickDuplicationEnabled(true);
		}
	}

	getFreeMoveMode(): boolean
	{
		return this.control.freeMoveMode;
	}

	setMode(mode: GizmoModes): void
	{
		this.control.setMode(mode);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGizmoChanged));
	}

	getMode(): GizmoModes
	{
		return this.control.mode as GizmoModes;
	}

	setSpace(space: Space): void
	{
		this.control.setSpace(space);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGizmoSpaceChanged));
	}

	getSpace(): Space
	{
		return this.control.space as Space;
	}

	setPivotMode(mode: GizmoPivotMode): void
	{
		this.control.setPivotMode(mode);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGizmoPivotModeChanged, { detail: { mode: mode } }));
	}

	getPivotMode(): GizmoPivotMode
	{
		return this.control.pivotMode as GizmoPivotMode;
	}

	setTranslationSnap(value: number): void
	{
		this.translationSnap = value;
		this.control.setTranslationSnap(this.translationSnap);
	}

	setRotationSnap(value: number): void
	{
		const radians = value * Math.PI / 180;
		this.rotationSnap = radians;
		this.control.setRotationSnap(this.rotationSnap);
	}

	setScaleSnap(value: number): void
	{
		this.scaleSnap = value;
		this.control.setScaleSnap(this.scaleSnap);
	}

	setSize(value: number): void
	{
		this.control.setSize(value);
	}

	setQuickDuplicationEnabled(state: boolean): void
	{
		this.control.quickDuplicationEnabled = state;
	}
}
